/**
 * Relationship Parser Utility
 * 
 * Parses relationship context strings to extract field-level information
 * and builds hierarchical graph structures for drill-down visualization
 */

import type { ParsedContext, HierarchicalNode, HierarchicalLink, FieldType } from '@/types/relationshipDetails';
import type { EntityRelationship } from '@/components/graphs/EntityRelationshipGraph';

/**
 * Parse relationship context string to extract field information
 * Context formats:
 * - node_{nodeId}_next_dialog_to_node_{targetNodeId}
 * - node_{nodeId}_speaker
 * - section_{sectionId}_field_{fieldId}_speaker
 * - element_{elementId}_linked_to_element_{targetElementId}
 */
export function parseRelationshipContext(context?: string): ParsedContext {
  if (!context) {
    return { relationshipType: 'unknown' };
  }

  const result: ParsedContext = { relationshipType: 'unknown' };

  // Parse dialog node references
  const nodeMatch = context.match(/node_([^_]+)_/);
  if (nodeMatch) {
    result.sourceFieldId = nodeMatch[1];
    result.sourceFieldType = 'node';
  }

  // Parse dialog option references
  const optionMatch = context.match(/option_([^_]+)_/);
  if (optionMatch) {
    result.sourceFieldId = optionMatch[1];
    result.sourceFieldType = 'option';
  }

  // Parse dialog condition references
  const conditionMatch = context.match(/condition_(\d+)_/);
  if (conditionMatch) {
    result.sourceFieldId = conditionMatch[1];
    result.sourceFieldType = 'condition';
  }

  // Parse section references (legacy dialog or object)
  const sectionMatch = context.match(/section_([^_]+)_/);
  if (sectionMatch) {
    result.sourceFieldId = sectionMatch[1];
    result.sourceFieldType = 'section';
  }

  // Parse field references
  const fieldMatch = context.match(/field_([^_]+)_/);
  if (fieldMatch) {
    result.sourceFieldId = fieldMatch[1];
    result.sourceFieldType = 'field';
  }

  // Parse element references (variables)
  const elementMatch = context.match(/element_([^_]+)_/);
  if (elementMatch) {
    result.sourceFieldId = elementMatch[1];
    result.sourceFieldType = 'element';
  }

  // Parse target node references
  const targetNodeMatch = context.match(/to_node_([^_]+)/);
  if (targetNodeMatch) {
    result.targetFieldId = targetNodeMatch[1];
    result.targetFieldType = 'node';
  }

  // Parse target element references
  const targetElementMatch = context.match(/to_element_([^_]+)/);
  if (targetElementMatch) {
    result.targetFieldId = targetElementMatch[1];
    result.targetFieldType = 'element';
  }

  // Determine relationship type
  if (context.includes('next_dialog')) {
    result.relationshipType = 'navigation';
  } else if (context.includes('speaker')) {
    result.relationshipType = 'speaker';
  } else if (context.includes('linked')) {
    result.relationshipType = 'linked_value';
  } else if (context.includes('blueprint_id')) {
    result.relationshipType = 'template';
  } else if (context.includes('highlight')) {
    result.relationshipType = 'reference';
  }

  return result;
}

/**
 * Get human-readable label for a field based on entity data
 */
export function getFieldLabel(
  entityType: string,
  entityData: any,
  fieldId: string,
  fieldType?: FieldType
): string {
  if (!entityData) return fieldId;

  try {
    switch (entityType) {
      case 'dialog': {
        // Try nodes (new structure)
        if (entityData.data?.nodes) {
          const node = entityData.data.nodes.find((n: any) => n.id === fieldId);
          if (node) {
            return `Node: ${node.name || node.node_identifier || fieldId}`;
          }
          
          // Check for options within nodes
          for (const node of entityData.data.nodes) {
            if (node.options) {
              const option = node.options.find((o: any) => o.id === fieldId);
              if (option) {
                return `Option: ${option.option_text?.substring(0, 30) || fieldId}`;
              }
            }
          }
        }
        
        // Try sections (legacy structure)
        if (entityData.data?.sections) {
          const section = entityData.data.sections.find((s: any) => s.id === fieldId);
          if (section) {
            return `Section: ${section.name || fieldId}`;
          }
          
          // Check for fields within sections
          for (const section of entityData.data.sections) {
            if (section.fields) {
              const field = section.fields.find((f: any) => f.id === fieldId);
              if (field) {
                return `Field: ${field.text?.substring(0, 30) || fieldId}`;
              }
            }
          }
        }
        break;
      }

      case 'glossary': {
        // Object sections and fields
        if (entityData.data && Array.isArray(entityData.data)) {
          const section = entityData.data.find((s: any) => s.id === fieldId);
          if (section) {
            return `Section: ${section.title || fieldId}`;
          }
          
          // Check for fields within sections
          for (const section of entityData.data) {
            if (section.fields) {
              const field = section.fields.find((f: any) => f.id === fieldId);
              if (field) {
                return `Field: ${field.label || fieldId}`;
              }
            }
          }
        }
        break;
      }

      case 'variable': {
        // Variable elements
        if (entityData.elements && Array.isArray(entityData.elements)) {
          const element = entityData.elements.find((e: any) => e.id === fieldId);
          if (element) {
            return `Element: ${element.name || element.element_key || fieldId}`;
          }
        }
        break;
      }
    }
  } catch (error) {
    console.error('Error getting field label:', error);
  }

  // Fallback to field ID
  return fieldId;
}

/**
 * Build hierarchical graph structure from relationships
 * This creates nodes and links for field-level visualization
 */
export function buildHierarchicalGraph(
  relationships: EntityRelationship[],
  entities: Map<string, any> // Map of entityId -> entity data
): { nodes: HierarchicalNode[]; links: HierarchicalLink[] } {
  const nodeMap = new Map<string, HierarchicalNode>();
  const links: HierarchicalLink[] = [];

  relationships.forEach((rel) => {
    const parsedContext = parseRelationshipContext(rel.relationship_context);
    
    // Create source node
    const sourceNodeId = parsedContext.sourceFieldId 
      ? `${rel.source_id}_${parsedContext.sourceFieldId}`
      : rel.source_id;
    
    if (!nodeMap.has(sourceNodeId)) {
      const sourceEntity = entities.get(rel.source_id);
      const label = parsedContext.sourceFieldId
        ? getFieldLabel(rel.source_type, sourceEntity, parsedContext.sourceFieldId, parsedContext.sourceFieldType)
        : sourceEntity?.name || rel.source_id;
      
      nodeMap.set(sourceNodeId, {
        id: sourceNodeId,
        name: label,
        type: rel.source_type,
        entityId: rel.source_id,
        entityType: rel.source_type,
        fieldId: parsedContext.sourceFieldId,
        fieldType: parsedContext.sourceFieldType,
        val: 1
      });
    }

    // Create target node
    const targetNodeId = parsedContext.targetFieldId
      ? `${rel.target_id}_${parsedContext.targetFieldId}`
      : rel.target_id;
    
    if (!nodeMap.has(targetNodeId)) {
      const targetEntity = entities.get(rel.target_id);
      const label = parsedContext.targetFieldId
        ? getFieldLabel(rel.target_type, targetEntity, parsedContext.targetFieldId, parsedContext.targetFieldType)
        : targetEntity?.name || rel.target_id;
      
      nodeMap.set(targetNodeId, {
        id: targetNodeId,
        name: label,
        type: rel.target_type,
        entityId: rel.target_id,
        entityType: rel.target_type,
        fieldId: parsedContext.targetFieldId,
        fieldType: parsedContext.targetFieldType,
        val: 1
      });
    }

    // Create link
    links.push({
      source: sourceNodeId,
      target: targetNodeId,
      relationshipType: parsedContext.relationshipType,
      context: rel.relationship_context,
      sourceFieldId: parsedContext.sourceFieldId,
      targetFieldId: parsedContext.targetFieldId
    });
  });

  // Calculate node sizes based on connections
  const nodeSizes = new Map<string, number>();
  links.forEach((link) => {
    nodeSizes.set(link.source, (nodeSizes.get(link.source) || 0) + 1);
    nodeSizes.set(link.target, (nodeSizes.get(link.target) || 0) + 1);
  });
  
  nodeMap.forEach((node, id) => {
    node.val = Math.max(1, nodeSizes.get(id) || 0);
  });

  return {
    nodes: Array.from(nodeMap.values()),
    links
  };
}

/**
 * Filter relationships for a specific entity
 * Returns only relationships where the entity is source or target
 */
export function filterRelationshipsForEntity(
  relationships: EntityRelationship[],
  entityId: string
): EntityRelationship[] {
  return relationships.filter(
    (rel) => rel.source_id === entityId || rel.target_id === entityId
  );
}

/**
 * Get all field-level connections for a specific entity
 * This extracts detailed field-to-field connections
 */
export function getFieldLevelConnections(
  entityId: string,
  entityType: string,
  entityData: any,
  relationships: EntityRelationship[]
): { sourceFields: any[]; targetFields: any[] } {
  const entityRelationships = filterRelationshipsForEntity(relationships, entityId);
  
  const sourceFields: any[] = [];
  const targetFields: any[] = [];

  entityRelationships.forEach((rel) => {
    const parsedContext = parseRelationshipContext(rel.relationship_context);
    
    if (rel.source_id === entityId && parsedContext.sourceFieldId) {
      sourceFields.push({
        fieldId: parsedContext.sourceFieldId,
        fieldType: parsedContext.sourceFieldType,
        label: getFieldLabel(entityType, entityData, parsedContext.sourceFieldId, parsedContext.sourceFieldType),
        targetEntityId: rel.target_id,
        targetEntityType: rel.target_type,
        relationshipType: parsedContext.relationshipType
      });
    }
    
    if (rel.target_id === entityId && parsedContext.targetFieldId) {
      targetFields.push({
        fieldId: parsedContext.targetFieldId,
        fieldType: parsedContext.targetFieldType,
        label: getFieldLabel(entityType, entityData, parsedContext.targetFieldId, parsedContext.targetFieldType),
        sourceEntityId: rel.source_id,
        sourceEntityType: rel.source_type,
        relationshipType: parsedContext.relationshipType
      });
    }
  });

  return { sourceFields, targetFields };
}
